'use client'

import { PencilSimpleIcon } from '@phosphor-icons/react'
import { Button } from './button'
import { cn } from '@/lib/tailwind'

interface EditButtonProps {
  /** Click handler */
  onClick: (e: React.MouseEvent) => void
  /** Whether the edit action is in progress */
  isEditing?: boolean
  /** Whether the button is disabled */
  disabled?: boolean
  /** Button variant: 'default' shows text, 'icon' shows only icon */
  variant?: 'default' | 'icon'
  /** Label text (default: "Edit") */
  label?: string
  /** Additional className */
  className?: string
}

/**
 * Standardized edit button with consistent styling.
 * Shows hover state and can indicate active editing mode.
 */
export function EditButton({
  onClick,
  isEditing = false,
  disabled = false,
  variant = 'default',
  label = 'Edit',
  className,
}: EditButtonProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick(e)
  }

  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClick}
        disabled={disabled}
        className={cn(
          'text-muted-foreground hover:text-foreground',
          isEditing && 'text-foreground bg-muted',
          className,
        )}
        aria-label={label}
      >
        <PencilSimpleIcon className="h-4 w-4" />
      </Button>
    )
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'text-muted-foreground hover:text-foreground',
        isEditing && 'text-foreground bg-muted',
        className,
      )}
    >
      <PencilSimpleIcon className="mr-2 h-4 w-4" />
      {label}
    </Button>
  )
}
